import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, Plus, X, CheckCircle2 } from "lucide-react";
import {
  closeBookingIssue,
  createBookingIssue,
  deleteBookingIssue,
  listBookingIssues,
  loadPicklists,
} from "@/lib/api";

const STATUS_STYLE = {
  open:          "bg-rose-100 text-rose-700",
  investigating: "bg-amber-100 text-amber-700",
  closed:        "bg-emerald-100 text-emerald-700",
};

/**
 * Per-booking QA / NCR issues panel.
 * Lists all lbs_booking_issues for a booking and lets users raise new
 * ones (R37 phase 15b). Sample / Temp issues capture a position + reading.
 */
export default function BookingIssuesPanel({ bookingId, siteId = null, currentUserId, currentRole }) {
  const [issues, setIssues]   = useState([]);
  const [types,  setTypes]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding]   = useState(false);
  const [draft, setDraft]     = useState({
    issue_type: "", title: "", body: "",
    sample_position: "", sample_temp_c: "",
  });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [r, p] = await Promise.all([
        listBookingIssues({ bookingId }),
        loadPicklists(siteId || null),
      ]);
      if (cancelled) return;
      if (r.error) toast.error(r.error.message);
      setIssues(r.data || []);
      setTypes(p?.issue_type || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [bookingId, siteId, tick]);

  const refresh = () => setTick((k) => k + 1);

  const isSampleOrTemp = draft.issue_type === "sample" || draft.issue_type === "temp";

  const submitNew = async () => {
    if (!draft.issue_type) { toast.error("Pick an issue type"); return; }
    const { error } = await createBookingIssue({
      booking_id: bookingId,
      ...draft,
    }, currentUserId, currentRole);
    if (error) { toast.error(error.message); return; }
    toast.success("Issue raised.");
    setAdding(false);
    setDraft({ issue_type: "", title: "", body: "", sample_position: "", sample_temp_c: "" });
    refresh();
  };

  const closeOne = async (id) => {
    const notes = window.prompt("Resolution notes (optional):", "");
    if (notes === null) return;  // cancel
    const { error } = await closeBookingIssue(id, notes, currentUserId);
    if (error) { toast.error(error.message); return; }
    toast.success("Issue closed.");
    refresh();
  };

  const removeOne = async (id) => {
    if (!window.confirm("Delete this issue permanently?")) return;
    const { error } = await deleteBookingIssue(id);
    if (error) { toast.error(error.message); return; }
    toast.success("Deleted.");
    refresh();
  };

  return (
    <div className="rounded-md border bg-white" data-testid="booking-issues-panel">
      <div className="flex items-center justify-between px-3 py-2 border-b">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <span className="text-sm font-semibold">QA / NCR issues</span>
          <Badge variant="secondary" className="text-[10px]" data-testid="issues-count">
            {issues.length}
          </Badge>
        </div>
        {!adding && (
          <Button size="sm" variant="outline" onClick={() => setAdding(true)} data-testid="issues-add-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> Raise issue
          </Button>
        )}
      </div>

      {adding && (
        <div className="px-3 py-3 border-b space-y-2 bg-slate-50" data-testid="issue-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-slate-500">Issue type *</Label>
              <Select value={draft.issue_type} onValueChange={(v) => setDraft((d) => ({ ...d, issue_type: v }))}>
                <SelectTrigger data-testid="issue-type-select"><SelectValue placeholder="Pick type" /></SelectTrigger>
                <SelectContent>
                  {types.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label || t.value}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Title</Label>
              <Input
                placeholder="Short summary"
                value={draft.title}
                onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                data-testid="issue-title-input"
              />
            </div>
          </div>
          {isSampleOrTemp && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-slate-500">Sample position</Label>
                <Input
                  type="number" min={1}
                  value={draft.sample_position}
                  onChange={(e) => setDraft((d) => ({ ...d, sample_position: e.target.value }))}
                  data-testid="issue-sample-pos"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Temperature (°C)</Label>
                <Input
                  type="number" step="0.1"
                  value={draft.sample_temp_c}
                  onChange={(e) => setDraft((d) => ({ ...d, sample_temp_c: e.target.value }))}
                  data-testid="issue-sample-temp"
                />
              </div>
            </div>
          )}
          <div>
            <Label className="text-xs text-slate-500">Details</Label>
            <Textarea
              rows={3}
              value={draft.body}
              onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
              placeholder="What did you observe? What's the corrective action?"
              data-testid="issue-body-input"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
            <Button size="sm" onClick={submitNew} data-testid="issue-submit-btn">Raise issue</Button>
          </div>
        </div>
      )}

      <div className="divide-y">
        {loading && <div className="px-3 py-4 text-sm text-slate-400">Loading…</div>}
        {!loading && issues.length === 0 && (
          <div className="px-3 py-6 text-sm text-slate-400 text-center" data-testid="issues-empty">
            No issues raised for this booking yet.
          </div>
        )}
        {issues.map((iss) => (
          <div
            key={iss.id}
            className="px-3 py-2.5"
            data-testid={`issue-row-${iss.id}`}
            data-issue-type={iss.issue_type}
            data-issue-status={iss.status}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <Badge className={`text-[10px] font-mono ${STATUS_STYLE[iss.status] || ""}`}>
                  {iss.status}
                </Badge>
                <Badge variant="outline" className="text-[10px]" data-testid={`issue-type-${iss.issue_type}`}>
                  {iss.issue_type}
                </Badge>
                <span className="text-sm font-medium truncate">
                  {iss.title || iss.body?.slice(0, 60) || "(no title)"}
                </span>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {iss.status !== "closed" && (
                  <Button size="sm" variant="outline" onClick={() => closeOne(iss.id)} data-testid={`issue-close-${iss.id}`}>
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Close
                  </Button>
                )}
                <Button size="icon" variant="ghost" onClick={() => removeOne(iss.id)} data-testid={`issue-delete-${iss.id}`}>
                  <X className="h-4 w-4 text-rose-500" />
                </Button>
              </div>
            </div>
            {iss.body && <div className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{iss.body}</div>}
            {(iss.sample_position || iss.sample_temp_c != null) && (
              <div className="text-[11px] text-slate-500 mt-1 font-mono">
                Sample #{iss.sample_position ?? "?"} · {iss.sample_temp_c ?? "?"} °C
              </div>
            )}
            {iss.resolution_notes && (
              <div className="text-[11px] text-emerald-700 mt-1">
                Resolution: {iss.resolution_notes}
              </div>
            )}
            <div className="text-[10px] text-slate-400 mt-1">
              Raised {new Date(iss.raised_at || iss.created_at).toLocaleString("en-AU", { hourCycle: "h23" })}
              {iss.raised_by_role ? ` · ${iss.raised_by_role}` : ""}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
